import { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getOwnAttendanceRecords } from '../../StudentRecord/services/studentRecordService';

const STATUSES = [
  { key: 'present', label: 'Present', bar: 'bg-green-500', tone: 'bg-green-50 text-green-600' },
  { key: 'late', label: 'Late', bar: 'bg-amber-500', tone: 'bg-amber-50 text-amber-600' },
  { key: 'absent', label: 'Absent', bar: 'bg-red-500', tone: 'bg-red-50 text-red-600' },
  { key: 'excused', label: 'Excused', bar: 'bg-[#4ea6d0]', tone: 'bg-blue-50 text-blue-600' },
];

const MODES = [
  { key: 'subject', label: 'By Subject' },
  { key: 'date', label: 'By Date' },
];

const sumRows = (rows) =>
  rows.reduce(
    (t, r) => ({
      present: t.present + r.present,
      late: t.late + r.late,
      absent: t.absent + r.absent,
      excused: t.excused + r.excused,
    }),
    { present: 0, late: 0, absent: 0, excused: 0 }
  );

const totalOf = (t) => t.present + t.late + t.absent + t.excused;

// The attendance average counts present days only: present / (present + late + absent). Late is not
// combined with present, and excused days are left out. Null when nothing is counted.
const averageOf = (t) => {
  const counted = t.present + t.late + t.absent;
  return counted > 0 ? (t.present / counted) * 100 : null;
};

const formatPercent = (value) => (value == null ? '—' : `${value.toFixed(2)}%`);

const formatDate = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

function BySubject({ subjects, rows }) {
  const bySubject = subjects.map((subject) => {
    const totals = sumRows(rows.filter((r) => r.subjectId === subject.subjectId));
    return { subject, totals, average: averageOf(totals) };
  });

  return bySubject.map(({ subject, totals, average }) => {
    const total = totalOf(totals);
    const summary = STATUSES.map((s) => `${s.label} ${totals[s.key]}`).join(', ');
    return (
      <div key={subject.subjectId} className="space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm font-medium text-gray-700 truncate">{subject.name}</p>
          <p className="shrink-0 text-sm font-medium text-[#0b4a71]">{formatPercent(average)}</p>
        </div>

        <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100" role="img" aria-label={summary}>
          {total > 0 &&
            STATUSES.filter((s) => totals[s.key] > 0).map((s) => (
              <div
                key={s.key}
                className={s.bar}
                style={{ width: `${(totals[s.key] / total) * 100}%` }}
                title={`${s.label}: ${totals[s.key]}`}
              />
            ))}
        </div>

        <p className="text-xs text-gray-500">{total > 0 ? summary : 'No attendance recorded'}</p>
      </div>
    );
  });
}

// One row per date, newest first: the status counts for that date, and the subject-by-subject
// breakdown when the row is opened.
function ByDate({ subjects, term }) {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setRecords(null);
    setError('');

    getOwnAttendanceRecords(Number(term))
      .then((result) => {
        if (mounted) setRecords(result);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load attendance by date');
      });

    return () => {
      mounted = false;
    };
  }, [term]);

  const subjectNames = useMemo(() => Object.fromEntries(subjects.map((s) => [s.subjectId, s.name])), [subjects]);

  const dates = useMemo(() => {
    const byDate = new Map();
    (records ?? []).forEach((r) => {
      if (!byDate.has(r.date)) byDate.set(r.date, []);
      byDate.get(r.date).push(r);
    });
    return [...byDate.entries()].map(([date, items]) => ({
      date,
      items: items.sort((a, b) => (subjectNames[a.subjectId] ?? '').localeCompare(subjectNames[b.subjectId] ?? '')),
      counts: Object.fromEntries(STATUSES.map((s) => [s.key, items.filter((i) => i.status === s.key).length])),
    }));
  }, [records, subjectNames]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (records === null) return <p className="text-center text-gray-500 py-6">Loading…</p>;
  if (dates.length === 0) return <p className="text-center text-gray-500 py-6">No attendance recorded yet.</p>;

  return (
    <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-100 pr-1">
      {dates.map(({ date, items, counts }) => (
        <details key={date} className="group py-2">
          <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
            <span className="text-sm font-medium text-gray-700">{formatDate(date)}</span>
            <span className="flex items-center gap-3">
              {STATUSES.filter((s) => counts[s.key] > 0).map((s) => (
                <span key={s.key} className="flex items-center gap-1 text-xs text-gray-600" title={s.label}>
                  <span className={`h-2 w-2 rounded-full ${s.bar}`} aria-hidden="true" />
                  <span className="sr-only">{s.label}</span>
                  {counts[s.key]}
                </span>
              ))}
              <ChevronDownIcon className="h-4 w-4 text-gray-400 transition group-open:rotate-180" aria-hidden="true" />
            </span>
          </summary>

          <ul className="mt-2 space-y-1.5">
            {items.map((item) => {
              const status = STATUSES.find((s) => s.key === item.status);
              return (
                <li key={item.subjectId} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600 truncate">{subjectNames[item.subjectId] ?? 'Subject'}</span>
                  <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${status?.tone ?? ''}`}>
                    {status?.label ?? item.status}
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      ))}
    </div>
  );
}

function StudentAttendanceView({ subjects, attendance, attendanceError, term }) {
  const [mode, setMode] = useState(MODES[0].key);

  if (attendanceError) {
    return <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{attendanceError}</p>;
  }

  const rows = attendance.filter((r) => r.term === Number(term));

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        No attendance recorded for Term {term} yet.
      </div>
    );
  }

  const overall = sumRows(rows);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] gap-3">
        <div className="bg-[#0b4a71] rounded-lg p-3 flex flex-col items-center justify-center text-center">
          <p className="text-sm text-white/70">Attendance</p>
          <p className="text-3xl sm:text-4xl font-bold text-white">{formatPercent(averageOf(overall))}</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STATUSES.map((s) => (
            <div key={s.key} className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className={`h-2 w-2 rounded-full ${s.bar}`} aria-hidden="true" />
                {s.label}
              </p>
              <p className="text-2xl font-bold text-[#0b4a71]">{overall[s.key]}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        {MODES.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            aria-pressed={mode === key}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition ${
              mode === key
                ? 'bg-[#4ea6d0] border-[#4ea6d0] text-white'
                : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-[#0b4a71]">
            {mode === 'subject' ? 'Attendance by subject' : 'Attendance by date'}
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
            {STATUSES.map((s) => (
              <span key={s.key} className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${s.bar}`} aria-hidden="true" />
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {mode === 'subject' ? <BySubject subjects={subjects} rows={rows} /> : <ByDate subjects={subjects} term={term} />}
      </div>
    </div>
  );
}

export default StudentAttendanceView;
